import { IJsonSchema } from '@estee/elc-form';
import { isCountryUS, isCountryCA } from './CountryUtils';
import { FIELD_TYPE } from '../constants/Constants';
import POSTAL_CODE_VALIDATIONS from '../constants/PostalCodeValidations';
import DEFAULT_ADDITIONAL_PROPERTIES_SCHEMA from '../constants/DefaultAdditionalPropertiesSchema';
import { IAddressSchemaProperties } from '../api/JsonSchemaApi';
import { IFieldDependencies } from '../domain/entities/Config';
import { IAddressAttributes } from '../domain/entities/Address';

export interface IJSONSchema extends IJsonSchema {
  properties: IAddressSchemaProperties;
}

export function getPostalCodeValidation(countryId: number): string {
  const country = POSTAL_CODE_VALIDATIONS.find(v => v.country === countryId);
  return country ? country.regex : '^\\d{4,10}$';
}

function getZipInputType(countryId: number): string {
  const selectedCountry = POSTAL_CODE_VALIDATIONS.find(({ country }) => (
    country === countryId));
  return selectedCountry ? selectedCountry.type : 'text';
}

export function getPostalCodeExample(countryId: number): string {
  const country = POSTAL_CODE_VALIDATIONS.find(v => v.country === countryId);
  return country && country.example ? country.example : '';
}

export function addAdditionalProperties(validationSchema: IJSONSchema): IJSONSchema {
  const schema = { ...validationSchema };
  let schemaHandlesAdditionalProperties = false;

  schemaHandlesAdditionalProperties = (Object.keys(schema.properties) as Array<keyof typeof schema.properties>)
    .some(key => schema.properties[key]?.additionalProperties || null);

  if (!schemaHandlesAdditionalProperties) {
    (Object.keys(schema.properties) as Array<keyof typeof schema.properties>).forEach(key => {
      if (!schema.properties[key]) return;

      const { additionalProperties } = schema.properties[key];
      const defaultAdditionalProperties = DEFAULT_ADDITIONAL_PROPERTIES_SCHEMA[key]
        && DEFAULT_ADDITIONAL_PROPERTIES_SCHEMA[key].additionalProperties;
      if (!additionalProperties && defaultAdditionalProperties) {
        schema.properties[key].additionalProperties = defaultAdditionalProperties;
      }
    });
  }

  return schema;
}

export const formatValidationSchema = (
  validationSchema: IAddressSchemaProperties,
  countryId?: number,
): IJSONSchema => {
  const schema: IJSONSchema = {
    $schema: 'http://json-schema.org/draft-04/schema#',
    properties: {},
    required: [],
    title: 'JSON schema for validating addresses',
    type: 'object',
  };

  schema.additionalProperties = {
    zipInputType: getZipInputType(countryId),
  };

  if (countryId) {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    schema.properties.postal_code = {
      ...validationSchema.postal_code,
      pattern: getPostalCodeValidation(countryId),
      required: !!validationSchema.postal_code ? validationSchema.postal_code.required ?? 1 : 1,
    };
    // TODO: Remove required hardcode and let it
    // rely on backend schema when that is corrected from their side (RCO-3159)
  }

  schema.properties = {
    ...validationSchema,
    ...schema.properties,
  };

  schema.required = (Object.keys(schema.properties) as Array<keyof typeof schema.properties>)
    .filter(key => schema.properties[key]?.required);
  // force state to required properties for legacy US & Canada schema
  if (isCountryUS(countryId) || isCountryCA(countryId)) {
    schema.required = [...schema.required, FIELD_TYPE.STATE];
  }

  return addAdditionalProperties(schema);
};

export const getFieldDependenciesBySchema = (schema: IAddressSchemaProperties): IFieldDependencies => {
  const dependencies: IFieldDependencies = {};

  Object.entries(schema || {}).map(([field, fieldSchema]) => {
    if (fieldSchema?.additionalProperties) {
      const {
        additionalProperties: {
          depends_on_field: dependsOn = '',
          r: targetUrl = '',
          response_key: responseKey = '',
        } = {},
      } = fieldSchema;

      if (dependsOn) {
        const depField = {
          fieldName: field as keyof IAddressAttributes,
          responseKey,
          targetUrl,
        };
        dependencies[dependsOn] = dependencies[dependsOn]
          ? [
            ...dependencies[dependsOn],
            depField,
          ]
          : [depField];
      }
    }
  });

  return dependencies;
};
